import React, { useState, useEffect, useCallback } from 'react';

import "../../../css/components/OrderInfo/EditOrderModal/EditOrderModal.css";

import { Message } from '../../../utils/Message';
import { delay } from '../../../utils';
import { roleConfig } from '../../../constants/roleConfig';
import { useUser } from '../../../hooks/useUser';
import RoleGuard from '../../RoleGuard/RoleGuard';
import { EditDeliverModalList } from '../../../constants/DeliverModal/DeliverModal';

import { getSMSCode } from '../../../service/api/SMS/apiSMS';
import { updateDeliver } from '../../../service/api/Deliver/apiDeliver';


const EditDeliverModal = ({ deliver, onClose, setDeliversData, companies = [], mode = 'personal' }) => {
  const { role: currentUserRole } = useUser();
  const [form, setForm] = useState({
    id: deliver?.id,
    email: deliver?.email,
    name: deliver?.name,
    phone: deliver?.phone,
    company: deliver?.company || null,
    status: deliver?.status,
    role: deliver?.role || 1,
    lineId: deliver?.lineId || "",
    verifyCode: "",
  });
  const [newCompanyInput, setNewCompanyInput] = useState('');
  const [showNewCompanyInput, setShowNewCompanyInput] = useState(false);
  
  // 原始電話號碼（用於比較是否變更）
  const [originalPhone] = useState(deliver?.phone || '');
  
  // SMS 驗證相關狀態
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsSending, setSmsSending] = useState(false);
  
  // 判斷電話是否已變更
  const isPhoneChanged = form.phone !== originalPhone;

  // SMS 倒數計時
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  // 發送簡訊驗證碼
  const handleSendSMS = useCallback(async () => {
    if (!form.phone || !/^09\d{8}$/.test(form.phone)) {
      Message('請先輸入正確的手機號碼', 'warning');
      return;
    }
    
    setSmsSending(true);
    try {
      const response = await getSMSCode({ phone: form.phone });
      if (response.data.status === 1) {
        Message('驗證碼已發送');
        setSmsCountdown(30);
      } else {
        Message(response.data.message || '發送失敗', 'warning');
      }
    } catch (error) {
      Message('發送驗證碼失敗', 'error');
    } finally {
      setSmsSending(false);
    }
  }, [form.phone]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    if (value === 'new') {
      setShowNewCompanyInput(true);
      setForm(prev => ({ ...prev, company: null }));
    } else {
      setShowNewCompanyInput(false);
      setForm(prev => ({ ...prev, company: value }));
    }
  };

  const handleAddNewCompany = () => {
    if (newCompanyInput.trim()) {
      setForm(prev => ({ ...prev, company: newCompanyInput }));
      setNewCompanyInput('');
      setShowNewCompanyInput(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 如果電話號碼已變更，需要驗證碼
    if (isPhoneChanged && !form.verifyCode) {
      Message('電話號碼已變更，請輸入簡訊驗證碼', 'warning');
      return;
    }
    
    // 個人模式下，company 欄位應為空或 null
    const submitData = {
      ...form,
      company: (mode === 'personal' || !form.company) ? null : form.company,
      // 只有電話變更時才帶驗證碼
      verifyCode: isPhoneChanged ? form.verifyCode : undefined,
    };
    console.log(submitData)
    const response = await updateDeliver(submitData);
    console.log(response.data);
    if (response.status === 200 && response.data.status === 1) {
      Message(response.data.message);
      await delay(1);
      onClose(null);
      setDeliversData(prev => prev.map(o => o.id === form.id ? response.data.result : o));
    } else {
      Message(response.data.message, 'warning');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="custom-modal-backdrop">
        <div className="custom-modal">
          <div className="custom-modal-header">
            <h5 className="modal-title">編輯司機 {deliver?.number}</h5>
            <button className="btn-close" onClick={(e) => { onClose(null); e.preventDefault(); }}></button>
          </div>

          <div className="custom-modal-body">
            {/* 基本資訊 */}
            {EditDeliverModalList.map(field => {
              // 公司欄位使用選單替代
              if (field.name === 'company') {
                return null;
              }

              // select 類型欄位
              if (field.type === 'select') {
                let filteredOptions = field.options || [];
                if (field.name === 'role' && currentUserRole) {
                  filteredOptions = field.options?.filter(opt => opt.value <= currentUserRole) || [];
                }

                return (
                  <div className="input-group mb-3" key={field.name}>
                    <span className="input-group-text">
                      {field.label}
                      {field.required && <span className="required-star">*</span>}
                    </span>
                    <select
                      className="form-select"
                      name={field.name}
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      required={field.required}
                    >
                      <option disabled value="">請選擇{field.label}</option>
                      {filteredOptions?.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              // text/email 類型欄位
              return (
                <div className="input-group mb-3" key={field.name}>
                  <span className="input-group-text">
                    {field.label}
                    {field.required && <span className="required-star">*</span>}
                  </span>
                  <input
                    type={field.type}
                    className="form-control"
                    name={field.name}
                    value={form[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder || ""}
                    required={field.required}
                    pattern={field.pattern}
                    title={field.title}
                  />
                </div>
              );
            })}

            {/* 公司選單 - 只在公司模式顯示 */}
            {mode === 'company' && (
              <div className="input-group mb-3">
                <span className="input-group-text">
                  公司
                  <span className="required-star">*</span>
                </span>
                <select
                  className="form-select"
                  value={form.company || ''}
                  onChange={handleCompanyChange}
                  required
                >
                  <option value="">請選擇公司</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.name}>
                      {company.name}
                    </option>
                  ))}
                  {/* 如果當前公司不在列表中，顯示它 */}
                  {form.company && !companies.some(c => c.name === form.company) && (
                    <option value={form.company} selected>
                      {form.company}
                    </option>
                  )}
                  <RoleGuard requiredRole={roleConfig['管理者']}> <option value="new">+ 新增公司</option> </RoleGuard>
                </select>
              </div>
            )}

            {/* 公司選單 - 個人模式時隱藏 */}
            {mode === 'personal' && (
              <input type="hidden" name="company" value="" />
            )}

            {/* 新增公司輸入框 */}
            {showNewCompanyInput && (
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="輸入新公司名稱"
                  value={newCompanyInput}
                  onChange={(e) => setNewCompanyInput(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={handleAddNewCompany}
                >
                  新增
                </button>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setShowNewCompanyInput(false);
                    setNewCompanyInput('');
                  }}
                >
                  取消
                </button>
              </div>
            )}

            {/* 簡訊驗證碼 - 只有電話號碼變更時顯示 */}
            {isPhoneChanged && (
              <div className="input-group mb-3">
                <span className="input-group-text">
                  簡訊驗證碼
                  <span className="required-star">*</span>
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="verifyCode"
                  value={form.verifyCode}
                  onChange={handleChange}
                  placeholder="請輸入簡訊驗證碼"
                  required
                  maxLength={6}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={handleSendSMS}
                  disabled={smsCountdown > 0 || smsSending || !form.phone}
                >
                  {smsSending ? '發送中...' : smsCountdown > 0 ? `${smsCountdown}秒後重發` : '發送驗證碼'}
                </button>
              </div>
            )}

            {/* 員工狀態 */}
            <RoleGuard requiredRole={roleConfig['主管']}>
              <div className="input-group mb-3">
                <span className="input-group-text">員工狀態</span>
                <select
                  className="form-select"
                  value={form.status === 1 ? "working" : form.status === 0 ? "resignation" : "suspension"}
                  onChange={e => setForm(prev => ({ ...prev, status: e.target.value === "working" ? 1 : e.target.value === "resignation" ? 0 : 2 }))}
                >
                  <option value="resignation">離職</option>
                  <option value="suspension">停職</option>
                  <option value="working">在職</option>
                </select>
              </div>
            </RoleGuard>
          </div>

          <div className="custom-modal-footer">
            <button className="btn btn-secondary" onClick={(e) => { onClose(null); e.preventDefault(); }}>取消</button>
            <button className="btn btn-gradient-primary" type="submit">儲存</button>
          </div>
        </div>
      </div>
    </form>
  );
};

export { EditDeliverModal };
